/**
 * Compact Build card at the top of the Design tab's right rail. Owns build
 * state AND its action: the running checklist (reusing the workspace progress
 * view-model), the built summary line, the pending-changes warning, and the
 * primary CTA that resolves it.
 */
import type { ReactElement } from 'react';
import { Check, FileSearch, Send } from 'lucide-react';
import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
} from '@volley/crucible-ipc-types/ipc-channels';
import {
  deriveWorkspaceProgressViewModel,
  type WorkspaceProgressPhaseRowModel,
} from '../../components/WorkspaceRightPanel';
import type { WorkspaceRunState } from '../../workspace/run-state';
import type { BuildCardModel, BuildCardPrimaryAction } from './design-status';

export interface DesignBuildActivity {
  runState: WorkspaceRunState | null;
  phaseProgress: PhaseProgressUpdate | undefined;
  activitySnapshot: ActivitySnapshot | null;
}

export interface BuildRailCardProps {
  model: BuildCardModel;
  /** Live build progress, threaded from the Workspace host. */
  buildActivity?: DesignBuildActivity | null;
  /** Run the Build card's single derived primary action. */
  onPrimaryAction: (action: NonNullable<BuildCardPrimaryAction>) => void;
}

function PhaseRow({ row }: { row: WorkspaceProgressPhaseRowModel }): ReactElement {
  const done = row.state === 'done' || row.state === 'done-partial';
  const current = row.state === 'in-progress' || row.state === 'blocked';
  return (
    <li className="flex items-center gap-2.5">
      {done ? (
        <Check size={12} className="shrink-0 text-emerald-300" aria-hidden="true" />
      ) : current ? (
        <span
          className="mx-0.5 size-2 shrink-0 animate-pulse rounded-full bg-crucible-accent"
          aria-hidden="true"
        />
      ) : (
        <span
          className="mx-0.5 size-1.5 shrink-0 rounded-full border border-white/20"
          aria-hidden="true"
        />
      )}
      <span
        className={`text-xs ${
          current ? 'text-crucible-fg' : done ? 'text-crucible-muted' : 'text-white/30'
        }`}
      >
        {row.label}
        {row.progress ? (
          <span className="ml-1 text-white/35">
            {row.progress.completed}/{row.progress.total}
          </span>
        ) : null}
      </span>
    </li>
  );
}

export function BuildRailCard({
  model,
  buildActivity,
  onPrimaryAction,
}: BuildRailCardProps): ReactElement {
  const viewModel =
    model.state === 'running' && buildActivity
      ? deriveWorkspaceProgressViewModel({
          runState: buildActivity.runState,
          phaseProgress: buildActivity.phaseProgress,
          activitySnapshot: buildActivity.activitySnapshot,
        })
      : null;

  return (
    <div
      className="shrink-0 border-b border-crucible-border p-3"
      data-testid="design-build-card"
      data-state={model.state}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-crucible-muted">
          Build
        </h2>
        {model.headDetail && (
          <span className="truncate font-mono text-[11px] text-crucible-muted">
            {model.headDetail}
          </span>
        )}
      </div>

      {model.state === 'none' && (
        <p className="text-xs leading-relaxed text-crucible-muted">
          No build yet. Approve the Build Plan to start.
        </p>
      )}

      {model.state === 'prep' && (
        <p className="flex items-center gap-2 text-xs leading-relaxed text-amber-200">
          <span
            className="size-1.5 shrink-0 animate-pulse rounded-full bg-amber-300"
            aria-hidden="true"
          />
          {model.prepLine ?? 'Preparing build…'}
        </p>
      )}

      {model.state === 'running' && (
        <div className="space-y-2">
          {viewModel ? (
            <>
              <ol className="space-y-1.5" data-testid="design-build-card-phases">
                {[viewModel.planRow, viewModel.approvePlanRow, ...viewModel.phaseRows].map(
                  (row) => (
                    <PhaseRow key={row.phaseNumber} row={row} />
                  ),
                )}
              </ol>
              {(viewModel.now.checkpoint || viewModel.now.phaseLabel) && (
                <p className="truncate border-t border-white/5 pt-2 font-mono text-[11px] text-crucible-muted">
                  {viewModel.now.checkpoint ?? viewModel.now.phaseLabel}
                </p>
              )}
            </>
          ) : (
            <p className="flex items-center gap-2 text-xs text-amber-200">
              <span
                className="size-1.5 animate-pulse rounded-full bg-amber-300"
                aria-hidden="true"
              />
              Build running…
            </p>
          )}
          {model.queuedLine && (
            <p
              className="flex items-center gap-2 text-xs text-amber-200"
              data-testid="design-build-card-queued"
            >
              <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
              {model.queuedLine}
            </p>
          )}
        </div>
      )}

      {model.state === 'built' && (
        <div className="space-y-2">
          {model.builtLine && (
            <p className="flex items-center gap-2 text-xs text-crucible-fg">
              <Check size={13} className="shrink-0 text-emerald-300" aria-hidden="true" />
              {model.builtLine}
            </p>
          )}
          {model.warnLine && (
            <p
              className="flex items-center gap-2 text-xs text-amber-200"
              data-testid="design-build-card-warn"
            >
              <span className="size-1.5 shrink-0 rounded-full bg-amber-300" aria-hidden="true" />
              {model.warnLine}
            </p>
          )}
          {model.primaryAction && (
            <button
              type="button"
              onClick={() => model.primaryAction && onPrimaryAction(model.primaryAction)}
              data-testid={
                model.primaryAction.kind === 'send-design-changes'
                  ? 'design-send-changes'
                  : 'design-review-build-activity'
              }
              className="mt-1 flex w-full items-center justify-center gap-2 rounded-lg bg-crucible-accent px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
            >
              {model.primaryAction.kind === 'send-design-changes' ? (
                <Send size={12} aria-hidden="true" />
              ) : (
                <FileSearch size={12} aria-hidden="true" />
              )}
              {model.primaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
